<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainInsightsAPI from 'dashboard/api/captain/insights';
import MessageApi from 'dashboard/api/inbox/message';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import InsightTaskModal from './InsightTaskModal.vue';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const { t } = useI18n();

const insight = ref(null);
const isLoading = ref(false);
const isGenerating = ref(false);

const conversationId = computed(() => props.conversation?.id);

const fetchInsight = async () => {
  if (!conversationId.value) return;
  isLoading.value = true;
  try {
    const response = await CaptainInsightsAPI.get({
      insightType: 'conversation_summary',
      conversationId: conversationId.value,
    });
    const payload = response.data.payload || [];
    insight.value = payload[0] || null;
  } catch {
    insight.value = null;
  } finally {
    isLoading.value = false;
  }
};

const generateInsight = async () => {
  if (isGenerating.value) return;
  isGenerating.value = true;
  try {
    const response = await CaptainInsightsAPI.generate(conversationId.value);
    insight.value = response.data.insight || null;
    useAlert(t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.GENERATED'));
  } catch (err) {
    const errorType = err?.response?.data?.error;
    if (errorType === 'not_enough_messages') {
      useAlert(t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.NOT_ENOUGH_MESSAGES'));
    } else {
      useAlert(t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.GENERATE_ERROR'));
    }
  } finally {
    isGenerating.value = false;
  }
};

// --- Action handlers ---

const processingAction = ref(null);
const showTaskModal = ref(false);
const taskModalPrefill = ref({});
const taskModalItem = ref({});

const handleOpenTaskModal = (prefill, item) => {
  taskModalPrefill.value = prefill || {};
  taskModalItem.value = item || {};
  showTaskModal.value = true;
};

const handleScheduleMessage = prefill => {
  const body = prefill?.body;
  if (!body) return;
  emitter.emit(BUS_EVENTS.OPEN_SCHEDULE_DIALOG, { message: body });
};

const handleAddNote = async prefill => {
  const content = prefill?.content;
  if (!content) return;
  await MessageApi.create({
    conversationId: conversationId.value,
    message: content,
    private: true,
  });
  useAlert(t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.NOTE_CREATED'));
};

const handleAction = async (action, item) => {
  if (processingAction.value) return;

  if (action.type === 'create_task') {
    handleOpenTaskModal(action.prefill, item);
    return;
  }

  if (action.type === 'schedule_message') {
    handleScheduleMessage(action.prefill);
    return;
  }

  processingAction.value = action.type;
  try {
    if (action.type === 'add_note') {
      await handleAddNote(action.prefill);
    }
  } catch {
    useAlert(t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTION_ERROR'));
  } finally {
    processingAction.value = null;
  }
};

// --- Fix 1: Fixed i18n labels instead of LLM-provided labels ---
const actionLabel = type => {
  const map = {
    create_task: t(
      'CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.CREATE_TASK'
    ),
    schedule_message: t(
      'CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.SCHEDULE_MESSAGE'
    ),
    add_note: t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.ADD_NOTE'),
  };
  return map[type] || type;
};

const summary = computed(() => insight.value?.data?.summary || '');

const sentimentConfig = computed(() => {
  const data = insight.value?.data || {};
  if (!data.sentiment) return null;
  const map = {
    positive: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.SENTIMENT.POSITIVE'),
      icon: 'i-lucide-smile',
      bgClass: 'bg-g-50 dark:bg-g-900',
      textClass: 'text-g-700 dark:text-g-200',
    },
    neutral: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.SENTIMENT.NEUTRAL'),
      icon: 'i-lucide-meh',
      bgClass: 'bg-y-50 dark:bg-y-900',
      textClass: 'text-y-700 dark:text-y-200',
    },
    negative: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.SENTIMENT.NEGATIVE'),
      icon: 'i-lucide-frown',
      bgClass: 'bg-r-50 dark:bg-r-900',
      textClass: 'text-r-700 dark:text-r-200',
    },
  };
  return map[data.sentiment] || null;
});

const actionItems = computed(() => insight.value?.data?.action_items || []);

const relativeTime = computed(() => {
  if (!insight.value?.created_at) return '';
  const created = insight.value.created_at;
  const date =
    typeof created === 'number' ? new Date(created * 1000) : new Date(created);
  const now = new Date();
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.JUST_NOW');
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
});

const actionIcon = type => {
  const map = {
    create_task: 'i-lucide-check-square',
    schedule_message: 'i-lucide-calendar-clock',
    add_note: 'i-lucide-sticky-note',
  };
  return map[type] || 'i-lucide-zap';
};

const itemPriorityClasses = priority => {
  const map = {
    high: 'bg-r-50 text-r-800 dark:bg-r-900 dark:text-r-200',
    medium: 'bg-y-50 text-y-800 dark:bg-y-900 dark:text-y-200',
    low: 'bg-g-50 text-g-800 dark:bg-g-900 dark:text-g-200',
  };
  return map[priority] || map.medium;
};

watch(conversationId, () => fetchInsight(), { immediate: true });
</script>

<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <Spinner />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!insight"
      class="flex flex-col items-center justify-center flex-1 px-4 text-center"
    >
      <span class="i-lucide-sparkles size-8 text-n-brand/40 mb-3" />
      <p class="text-xs font-medium text-n-slate-11 mb-1">
        {{ t('CAPTAIN.BRAIN.INSIGHTS_TAB_EMPTY_TITLE') }}
      </p>
      <p class="text-[10px] text-n-slate-9 mb-4 max-w-[200px]">
        {{ t('CAPTAIN.BRAIN.INSIGHTS_TAB_EMPTY_DESCRIPTION') }}
      </p>
      <button
        class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-n-brand to-n-ruby-9 hover:opacity-90 rounded-lg transition-opacity cursor-pointer"
        :disabled="isGenerating"
        @click="generateInsight"
      >
        <Spinner v-if="isGenerating" class="size-3.5" />
        <span v-else class="i-lucide-sparkles size-3.5" />
        {{
          isGenerating
            ? t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.GENERATING')
            : t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.GENERATE')
        }}
      </button>
    </div>

    <!-- Insight content -->
    <div v-else class="flex-1 overflow-y-auto slim-scrollbar p-3 space-y-3">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <span class="brain-wordmark text-xs">
          br<span class="brain-highlight">ai</span>n
        </span>
        <span class="text-[10px] text-n-slate-9">{{ relativeTime }}</span>
      </div>

      <!-- Summary -->
      <div v-if="summary" class="bg-n-alpha-2 rounded-lg p-3">
        <h4
          class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider mb-1"
        >
          {{ t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.SUMMARY') }}
        </h4>
        <p class="text-xs text-n-slate-12 leading-relaxed m-0">
          {{ summary }}
        </p>
      </div>

      <!-- Sentiment -->
      <div v-if="sentimentConfig" class="flex items-center gap-2">
        <span
          class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
        >
          {{ t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.SENTIMENT') }}
        </span>
        <span
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
          :class="[sentimentConfig.bgClass, sentimentConfig.textClass]"
        >
          <Icon :icon="sentimentConfig.icon" class="size-3" />
          {{ sentimentConfig.label }}
        </span>
      </div>

      <!-- Action Items -->
      <div v-if="actionItems.length" class="space-y-2">
        <h4
          class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
        >
          {{ t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTION_ITEMS') }}
        </h4>
        <div
          v-for="(item, idx) in actionItems"
          :key="idx"
          class="bg-n-alpha-2 rounded-lg p-2.5 space-y-1.5"
        >
          <div class="flex items-start gap-1.5">
            <span
              class="inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold shrink-0 mt-px"
              :class="itemPriorityClasses(item.priority)"
            >
              {{ item.priority }}
            </span>
            <span class="text-xs text-n-slate-12">{{ item.text }}</span>
          </div>
          <div
            v-if="item.actions?.length"
            class="flex flex-wrap gap-1 ml-6"
          >
            <button
              v-for="action in item.actions"
              :key="action.type"
              class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded border border-n-weak bg-n-background text-n-slate-11 hover:bg-n-alpha-2 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!!processingAction"
              @click="handleAction(action, item)"
            >
              <Spinner
                v-if="processingAction === action.type"
                class="size-2.5"
              />
              <Icon v-else :icon="actionIcon(action.type)" class="size-2.5" />
              {{ actionLabel(action.type) }}
            </button>
          </div>
        </div>
      </div>

      <!-- Re-analyze button -->
      <div class="pt-2 border-t border-n-weak">
        <button
          class="inline-flex items-center gap-1 text-[10px] text-n-slate-9 hover:text-n-brand transition-colors cursor-pointer"
          :disabled="isGenerating"
          @click="generateInsight"
        >
          <Icon icon="i-lucide-refresh-cw" class="size-3" />
          {{ t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.REGENERATE') }}
        </button>
      </div>
    </div>

    <!-- Task creation modal -->
    <InsightTaskModal
      v-if="showTaskModal"
      :prefill="taskModalPrefill"
      :action-item="taskModalItem"
      :conversation-id="conversationId"
      @close="showTaskModal = false"
      @created="showTaskModal = false"
    />
  </div>
</template>

<style scoped>
.brain-wordmark {
  font-weight: 700;
  letter-spacing: -0.3px;
}

.brain-highlight {
  font-weight: 800;
  background: linear-gradient(135deg, rgb(var(--iris-9)), rgb(var(--ruby-9)));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>
